import { Spacer } from "@iewc/Components/Spacer";
import AlertCircle from "@iewc/MobiusOverrides/Icons/AlertCircle";
import { getAvailabilityComponent } from "@iewc/Overrides/Components/ProductAvailability";
import customLoadStoolItems from "@iewc/Store/Handlers/Custom/CustomLoadStoolItems";
import setSpoolModalOpen from "@iewc/Store/Handlers/Custom/CustomSetSpoolModal";
import { setGA4Event } from "@iewc/Store/Handlers/Utilities/CustomTracking";
import { colorStrings } from "@iewc/Utilities/variables";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import { HasOnComplete, makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import { getIsShareEntity } from "@insite/client-framework/ServerSideRendering";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setAddToListModalIsOpen from "@insite/client-framework/Store/Components/AddToListModal/Handlers/SetAddToListModalIsOpen";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import addToWishList, {
    AddToWishListParameter,
} from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import addToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddToCart";
import reorder from "@insite/client-framework/Store/Pages/OrderDetails/Handlers/Reorder";
import translate from "@insite/client-framework/Translate";
import {
    CartLineModel,
    OrderLineModel,
    OrderModel,
    WishListSettingsModel,
} from "@insite/client-framework/Types/ApiModels";
import CardContainer from "@insite/content-library/Components/CardContainer";
import ProductAddedToCartMessage from "@insite/content-library/Components/ProductAddedToCartMessage";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import VmiBinDetailTypeLink from "@insite/content-library/Components/VmiBinDetailTypeLink";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import OverflowMenu, { OverflowMenuPresentationProps } from "@insite/mobius/OverflowMenu";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    order?: OrderModel;
    extendedStyles?: OrderLinesListStyles;
}

interface ProductPriceParameter {
    productId: string;
    unitOfMeasure: string;
    qtyOrdered: number;
}

const mapStateToProps = (state: ApplicationState) => {
    const cartLines = getCurrentCartState(state).value?.cartLines;
    return {
        wishListSettings: getSettingsCollection(state).wishListSettings,
        enableVat: getSettingsCollection(state).productSettings.enableVat,
        vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
        vmiBinDetailsPageUrl: getPageLinkByPageType(state, "VmiBinDetailsPage")?.url,
        canReorderItems: getSettingsCollection(state).orderSettings.canReorderItems,
        // Verndale Customization
        isAuthenticated: state.context?.session?.isAuthenticated && !state.context?.session?.isGuest,
        billTo: getBillToState(state, state.context?.session?.billToId).value,
        isCartUpdating: getCurrentCartState(state).isLoading,
        cartLines,
    };
};

const mapDispatchToProps = {
    setAddToListModalIsOpen,
    addToWishList,
    reorder,
    addToCart: makeHandlerChainAwaitable<Parameters<typeof addToCart>[0], CartLineModel>(addToCart),
    // Verndale Customization
    customLoadStoolItems: makeHandlerChainAwaitable<
        Parameters<typeof customLoadStoolItems>[0],
        ProductPriceParameter[]
    >(customLoadStoolItems),
    setSpoolModalOpen,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface OrderLineNotesStyles {
    typographyStyles?: TypographyProps;
    gridItemStyles?: GridItemProps;
}

export interface OrderLinesListStyles {
    gridContainer?: GridContainerProps;
    titleGridItem?: GridItemProps;
    titleText?: TypographyProps;
    orderLineCardContainer?: GridContainerProps;
    orderLineCardGridItem?: GridItemProps;
    orderLineCardGridContainer?: GridContainerProps;
    orderLineCardImageGridItem?: GridItemProps;
    productImageLink?: LinkPresentationProps;
    orderLineCardImage?: LazyImageProps;
    orderLineCardInfoGridItem?: GridItemProps;
    orderLineCardInfoGridContainer?: GridContainerProps;
    orderLineCardAddToListGridItem?: GridItemProps;
    orderLineCardActionsWide?: HiddenProps;
    orderLineCardActionsNarrow?: HiddenProps;
    overflowMenu?: OverflowMenuPresentationProps;
    addToListClickable?: ClickablePresentationProps;
    orderLineCardAddToListButton?: ButtonPresentationProps;
    productBrandStyles?: ProductBrandStyles;
    productInfoLink?: LinkPresentationProps;
    productInfoDescription?: TypographyProps;
    productInfoSectionOptionsGridItem?: GridItemProps;
    productInfoSectionOptionsList?: InjectableCss;
    productInfoSectionOptionsListItem?: InjectableCss;
    productInfoPartNumbersGridItem?: GridItemProps;
    productInfoPartNumbersGridContainer?: GridContainerProps;
    productInfoSectionOptionsText?: InjectableCss;
    productInfoGridItem?: GridItemProps;
    productInfoGridContainer?: GridContainerProps;
    productInfoBrandDescriptionGridItem?: GridItemProps;
    productInfoBrandDescriptionGridContainer?: GridContainerProps;
    productInfoBrandGridItem?: GridItemProps;
    productInfoDescriptionGridItem?: GridItemProps;
    productDescriptionStyles?: ProductDescriptionStyles;
    productInfoErpNumberGridItem?: GridItemProps;
    erpNumberHeadingAndText?: SmallHeadingAndTextStyles;
    productInfoManufacturerItemGridItem?: GridItemProps;
    productInfoVmiBinNumberGridItem?: GridItemProps;
    manufacturerHeadingAndText?: SmallHeadingAndTextStyles;
    vmiBinNumberHeadingAndText?: SmallHeadingAndTextStyles;
    productInfoCustomerProductGridItem?: GridItemProps;
    customerProductNumberHeadingAndText?: SmallHeadingAndTextStyles;
    orderLineInfoPromotionGridItem?: GridItemProps;
    orderLineInfoPromotionList?: InjectableCss;
    orderLineInfoPromotionText?: TypographyProps;
    orderLineInfoGridItem?: GridItemProps;
    orderLineInfoGridContainer?: GridContainerProps;
    orderLinePriceGridItem?: GridItemProps;
    priceHeadingAndText?: SmallHeadingAndTextStyles;
    vatLabelText?: TypographyPresentationProps;
    secondaryPriceText?: TypographyPresentationProps;
    secondaryVatLabelText?: TypographyPresentationProps;
    productInfoQtyOrderedGridItem?: GridItemProps;
    productInfoQtyOrderedHeadingAndText?: SmallHeadingAndTextStyles;
    productInfoQtyShippedGridItem?: GridItemProps;
    productInfoQtyShippedHeadingAndText?: SmallHeadingAndTextStyles;
    productInfoSubtotalGridItem?: GridItemProps;
    subtotalWithoutVatHeadingAndText?: SmallHeadingAndTextStyles;
    productInfoSubtotalHeadingAndText?: SmallHeadingAndTextStyles;
    orderLineNotes?: OrderLineNotesStyles;
    productInfoTotalsGridItem?: GridItemProps;
    productInfoTotalsGridContainer?: GridContainerProps;
    smallHeadingAndText: SmallHeadingAndTextStyles;
    trackingLink?: LinkPresentationProps;
    tracking?: TypographyProps;
    trackingWrapper?: InjectableCss;
    trackingWrapperGridItem?: GridItemProps;
}

export const orderLinesListStyles: OrderLinesListStyles = {
    gridContainer: {
        gap: 10,
    },
    titleGridItem: {
        width: 12,
    },
    titleText: {
        variant: "h5",
        as: "h2",
        css: css`
            @media print {
                font-size: 15px;
                padding-top: 15px;
                margin-bottom: 5px;
            }
            padding-top: 30px;
            margin-bottom: 0.5rem;
        `,
    },
    orderLineCardContainer: {
        gap: 10,
    },
    orderLineCardGridItem: {
        width: 12,
    },
    orderLineCardImageGridItem: {
        width: 2,
        css: css`
            @media print {
                max-height: 83px;
                max-width: 83px;
                padding: 15px 0 0 0;
            }
        `,
    },
    orderLineCardInfoGridItem: {
        width: [8, 8, 7, 8, 8],
        printWidth: 10,
        css: css`
            @media print {
                &&&& {
                    max-width: 100%;
                }
            }
        `,
    },
    orderLineCardInfoGridContainer: {
        gap: 10,
    },
    orderLineCardAddToListGridItem: {
        width: [2, 2, 3, 2, 2],
        printWidth: 0,
    },
    orderLineCardActionsWide: {
        above: "sm",
    },
    orderLineCardActionsNarrow: {
        below: "md",
    },
    productInfoSectionOptionsGridItem: {
        width: 12,
    },
    productInfoPartNumbersGridItem: {
        width: 12,
    },
    productInfoGridItem: {
        width: [12, 12, 12, 5, 5],
        printWidth: 6,
    },
    productInfoGridContainer: {
        gap: 5,
    },
    productInfoBrandDescriptionGridItem: {
        width: 12,
    },
    productInfoBrandDescriptionGridContainer: {
        gap: 5,
    },
    productInfoBrandGridItem: {
        width: 12,
    },
    productInfoDescriptionGridItem: {
        width: 12,
    },
    productInfoErpNumberGridItem: {
        width: [12, 12, 6, 6, 6],
    },
    productInfoManufacturerItemGridItem: {
        width: [12, 12, 6, 6, 6],
    },
    productInfoVmiBinNumberGridItem: {
        width: [12, 12, 6, 6, 6],
    },
    productInfoCustomerProductGridItem: {
        width: [12, 12, 12, 12, 12],
        printWidth: 12,
    },
    orderLineInfoPromotionGridItem: {
        width: 12,
    },
    orderLineInfoPromotionText: {
        size: 12,
        italic: true,
    },
    orderLineInfoGridItem: {
        width: [12, 12, 12, 7, 7],
        printWidth: 6,
    },
    orderLineInfoGridContainer: {
        gap: 5,
    },
    orderLinePriceGridItem: {
        width: [12, 12, 12, 12, 12],

        css: css`
            flex-direction: column;
        `,
    },
    vatLabelText: {
        size: 12,
    },
    secondaryVatLabelText: {
        size: 12,
    },
    productInfoQtyOrderedGridItem: {
        width: [6, 6, 4, 3, 3],
        printWidth: 4,
    },
    productInfoQtyShippedGridItem: {
        width: [6, 6, 4, 3, 3],
        printWidth: 4,
    },
    productInfoSubtotalGridItem: {
        width: [12, 12, 4, 6, 6],
        printWidth: 4,
    },
    orderLineNotes: {
        gridItemStyles: {
            width: 12,
        },
    },
    productInfoTotalsGridItem: {
        width: [12, 12, 12, 12, 12],
        css: css`
            padding-top: 10px;
        `,
    },
    productInfoTotalsGridContainer: {
        gap: 10,
    },
    productInfoQtyOrderedHeadingAndText: {
        extendedStyles: {
            wrapper: {
                css: css`
                    padding-right: 10px;
                `,
            },
            heading: {
                variant: "legend",
                weight: "bold",
            },

            text: {
                css: css`
                    text-align: center;
                `,
            },
        },
    },
    smallHeadingAndText: {
        extendedStyles: {
            wrapper: {
                css: css`
                    display: flex;
                `,
            },
        },
    },
    productInfoQtyShippedHeadingAndText: {
        extendedStyles: {
            wrapper: {
                css: css`
                    padding-right: 10px;
                `,
            },
            heading: {
                variant: "legend",
                weight: "bold",
            },

            text: {
                css: css`
                    text-align: center;
                `,
            },
        },
    },
    productInfoSubtotalHeadingAndText: {
        wrapper: {
            css: css`
                padding-right: 10px;
            `,
        },
        heading: {
            variant: "legend",
            weight: "bold",
        },

        text: {
            weight: "bold",
            css: css`
                text-align: center;
            `,
        },
    },
    priceHeadingAndText: {
        extendedStyles: {
            heading: {
                weight: "bold",
            },
            text: {
                weight: "bold",
            },
        },
    },
    customerProductNumberHeadingAndText: {
        extendedStyles: {
            heading: {
                weight: "bold",
            },
        },
    },
    erpNumberHeadingAndText: {
        extendedStyles: {
            heading: {
                weight: "bold",
            },
        },
    },
    manufacturerHeadingAndText: {
        extendedStyles: {
            heading: {
                weight: "bold",
            },
        },
    },
    vmiBinNumberHeadingAndText: {
        extendedStyles: {
            heading: {
                weight: "bold",
            },
        },
    },
    trackingLink: {
        typographyProps: {
            size: 14,
        },
    },
    tracking: {
        css: css`
            list-style: circle outside none;
            margin-left: 25px;
        `,
    },

    // Verndale customization
    orderLineCardAddToListButton: {
        css: css`
            width: 100%;
        `,
    },
    trackingWrapper: {
        css: css`
            border: 2px solid ${getColor(colorStrings.secondaryMain)};
            padding: 5px 10px;

            p {
                margin: 0;
            }
        `,
    },
    trackingWrapperGridItem: {
        width: 12,
        css: css`
            border: 2px solid ${getColor(colorStrings.secondaryMain)};
            padding: 5px 10px;

            p {
                margin: 0;
            }
        `,
    },
};

const Tracking = ({ list }: any) => {
    const [isExpanded, setIsExpanded] = useState(false);

    if (!list?.length) {
        return (
            <Typography as="p" variant="p" size={14}>
                <strong>{translate("Shipment Tracking")}: </strong>
                {translate("Not Available")}
            </Typography>
        );
    }

    if (list.length === 1) {
        return (
            <Typography as="p" variant="p" size={14}>
                <strong>{translate("Shipment Tracking")}: </strong>
                {list[0].carrierTrackingUrl && list[0].carrierTrackingUrl !== "" && (
                    <Link
                        className="testo"
                        {...orderLinesListStyles.trackingLink}
                        href={list[0].carrierTrackingUrl}
                        target="_blank"
                    >
                        {list[0].carrierTrackingNumber}
                    </Link>
                )}
                {!list[0].carrierTrackingUrl && list[0].carrierTrackingUrl === "" && (
                    <Typography>{list[0].carrierTrackingNumber}</Typography>
                )}
            </Typography>
        );
    }

    return (
        <>
            <Typography as="p" variant="p" size={14}>
                <strong>{translate("Shipment Tracking")}: </strong>
                <Link {...orderLinesListStyles.trackingLink} onClick={() => setIsExpanded(!isExpanded)}>
                    {translate(isExpanded ? "Hide Tracking" : "Show Tracking")}
                </Link>
                {isExpanded && (
                    <Typography as="ul" {...orderLinesListStyles.tracking}>
                        {list.map((tracking: any) => (
                            <Typography size={14} as="li" key={tracking.carrierTrackingNumber}>
                                <Link
                                    {...orderLinesListStyles.trackingLink}
                                    href={tracking.carrierTrackingUrl}
                                    target="_blank"
                                >
                                    {tracking.carrierTrackingNumber}
                                </Link>
                            </Typography>
                        ))}
                    </Typography>
                )}
            </Typography>
        </>
    );
};

const OrderLineProductInfo = ({
    orderLine,
    styles,
    vmiLocationId,
}: {
    orderLine: OrderLineModel;
    styles: OrderLinesListStyles;
    vmiLocationId: string;
}) => {
    let sectionOptions: JSX.Element[] = [];
    if (orderLine.sectionOptions) {
        sectionOptions = orderLine.sectionOptions.map(option => (
            <li {...styles.productInfoSectionOptionsListItem} key={option.sectionOptionId.toString()}>
                <Typography {...styles.productInfoSectionOptionsText}>
                    {option.sectionName}: {option.optionName}
                </Typography>
            </li>
        ));
    }

    const isNCNR = (orderLine as any)?.product?.properties?.["isCustNCNR"]?.toLowerCase() === "true";
    let nonReturnableTextComponent;
    if (isNCNR) {
        nonReturnableTextComponent = getAvailabilityComponent({
            titleKey: "ProductAvailability_NonReturnable_Title_Tooltip",
            contentKey: "ProductAvailability_AvailableInventory_Content_Tooltip",
            styles: {},
            sharedTooltipTextStyle: [],
            color: "danger",
            iconSrc: AlertCircle,
            iconColor: "danger",
            tooltipText: <>{translate("Non-Returnable")}</>,           
        });
    }

    return (
        <GridItem {...styles.productInfoGridItem}>
            <GridContainer {...styles.productInfoGridContainer}>
                <GridItem {...styles.productInfoBrandDescriptionGridItem}>
                    <GridContainer {...styles.productInfoBrandDescriptionGridContainer}>
                        {orderLine.brand && (
                            <GridItem {...styles.productInfoBrandGridItem}>
                                <ProductBrand brand={orderLine.brand} extendedStyles={styles.productBrandStyles} />
                            </GridItem>
                        )}
                        <GridItem {...styles.productInfoDescriptionGridItem}>
                            {vmiLocationId && orderLine.isActiveVmiBin && (
                                <VmiBinDetailTypeLink
                                    title={orderLine.shortDescription}
                                    locationId={vmiLocationId}
                                    id={orderLine.vmiBinId}
                                />
                            )}
                            {vmiLocationId && !orderLine.isActiveVmiBin && (
                                <Typography as="p">{orderLine.shortDescription}</Typography>
                            )}
                            {!vmiLocationId && (
                                <ProductDescription
                                    product={orderLine}
                                    extendedStyles={styles.productDescriptionStyles}
                                />
                            )}
                        </GridItem>
                    </GridContainer>
                </GridItem>
                {sectionOptions.length > 0 && (
                    <GridItem {...styles.productInfoSectionOptionsGridItem}>
                        <ul {...styles.productInfoSectionOptionsList}>{sectionOptions}</ul>
                    </GridItem>
                )}
                <GridItem {...styles.productInfoPartNumbersGridItem}>
                    <div>
                        {orderLine.customerProductNumber &&
                            (orderLine as any).productInfo?.properties.isCmiProduct &&
                            (orderLine as any).productInfo?.properties.isCmiProduct.toLowerCase() === "true" && (
                                <Typography as="p" size={14}>
                                    <strong>{translate("Customer Material #")}: </strong>
                                    {orderLine.customerProductNumber}
                                </Typography>
                            )}
                        {/* Verndale customization */}
                        {!(orderLine as any).productInfo?.properties.isCmiProduct && (
                            <Typography as="p" size={14}>
                                <strong>{siteMessage("IEWC_Part_#")}: </strong>
                                {orderLine.productErpNumber}
                            </Typography>
                        )}
                        {(orderLine as any).productInfo?.properties.isCmiProduct &&
                            (orderLine as any).productInfo?.properties.isCmiProduct.toLowerCase() === "true" && (
                                <Typography as="p" size={14}>
                                    <strong>{siteMessage("IEWC_Part_#")}: </strong>
                                    {(orderLine as any).productInfo?.name ?? (orderLine as any).productInfo?.erpNumber}
                                </Typography>
                            )}
                        {orderLine.manufacturerItem && !(orderLine as any).productInfo?.properties.isCmiProduct && (
                            <Typography as="p" size={14}>
                                <strong>{translate("MFG #")}: </strong>
                                {orderLine.manufacturerItem}
                            </Typography>
                        )}
                        {/* Verndale customization */}
                        <Typography as="p" size={14}>
                            <strong>{translate("Line")}: </strong>
                            {orderLine.lineNumber}
                        </Typography>
                        {orderLine.status && (
                            <Typography as="p" size={14}>
                                <strong>{translate("Status")}: </strong>
                                {orderLine.status}
                            </Typography>
                        )}
                    </div>
                </GridItem>
                <GridItem {...styles.trackingWrapperGridItem}>
                    <Tracking list={(orderLine as any).shipmentTrackings} />
                    {nonReturnableTextComponent}
                </GridItem>
            </GridContainer>
        </GridItem>
    );
};

const mapStateToOrderLineInfoProps = (state: ApplicationState) => ({
    enableVat: getSettingsCollection(state).productSettings.enableVat,
    vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
    showPricing: state.context.showPricing,
});

const OrderLineInfoProto = ({
    orderLine,
    order,
    enableVat,
    vatPriceDisplay,
    styles,
    showPricing,
}: {
    orderLine: OrderLineModel;
    order: OrderModel;
    enableVat: boolean;
    vatPriceDisplay: string;
    styles: OrderLinesListStyles;
    showPricing?: boolean;
}) => {
    let promotions: JSX.Element[] = [];
    if (order.orderPromotions) {
        promotions = order.orderPromotions
            .filter(promotion => promotion.orderHistoryLineId === orderLine.id)
            .map(promotion => (
                <Typography {...styles.orderLineInfoPromotionText} as="li" key={promotion.id}>
                    {promotion.name}
                </Typography>
            ));
    }

    return (
        <GridItem {...styles.orderLineInfoGridItem}>
            <GridContainer {...styles.orderLineInfoGridContainer}>
                <GridItem {...styles.productInfoTotalsGridItem}>
                    <div>
                        <Typography as="p" size={14}>
                            {orderLine.properties.shipVia}
                        </Typography>
                        <Typography as="p" size={14}>
                            <strong>{translate("Est. Delivery")}: </strong>
                            {orderLine.properties.estimatedDelivery && orderLine.properties.estimatedDelivery !== ""
                                ? orderLine.properties.estimatedDelivery
                                : translate("Not Available")}
                        </Typography>
                        <Typography as="p" size={14}>
                            <strong>{translate("Estimated Shipping Quantity")}: </strong>
                            {[orderLine.qtyOrdered, orderLine.properties.shipQtyUnitOfMeasure].join(" ")}
                        </Typography>
                        {orderLine.unitOfMeasure && !(orderLine as any).productInfo?.properties.isCmiProduct && (
                            <Typography as="p" size={14}>
                                <strong>{translate("Unit price")}: </strong>
                                {`${orderLine.unitRegularPriceDisplay}/${parseFloat(
                                    (orderLine as any).productInfo?.properties?.perQtyValue ?? "1",
                                ).toString()} ${orderLine.unitOfMeasure}`}
                            </Typography>
                        )}
                        {Object.keys((orderLine as any).productInfo?.properties).length > 0 &&
                            (orderLine as any).productInfo?.properties?.isCmiProduct &&
                            (orderLine as any).productInfo?.properties?.isCmiProduct.toLowerCase() === "true" && (
                                <Typography as="p" size={14}>
                                    <strong>{translate("Unit price")}: </strong>
                                    {`${orderLine.unitRegularPriceDisplay}/${parseFloat(
                                        (orderLine as any).properties?.unitPricePer,
                                    ).toString()} ${(orderLine as any).properties?.shipQtyUnitOfMeasure}`}
                                </Typography>
                            )}
                        <Typography as="p" size={14}>
                            <strong>{translate("Amount")}: </strong>
                            {orderLine.totalRegularPriceDisplay}
                        </Typography>
                    </div>
                </GridItem>
                {promotions.length > 0 && (
                    <GridItem {...styles.orderLineInfoPromotionGridItem}>
                        <ul {...styles.orderLineInfoPromotionList}>{promotions}</ul>
                    </GridItem>
                )}
            </GridContainer>
        </GridItem>
    );
};

const OrderLineInfo = connect(mapStateToOrderLineInfoProps)(OrderLineInfoProto);

const OrderLineNotes = ({
    orderLine,
    extendedStyles,
}: {
    orderLine: OrderLineModel;
    extendedStyles?: OrderLineNotesStyles;
}) => {
    return orderLine.notes ? (
        <GridItem {...extendedStyles?.gridItemStyles}>
            <Typography {...extendedStyles?.typographyStyles}>{orderLine.notes}</Typography>
        </GridItem>
    ) : null;
};

const OrderLineCard = (props: {
    orderLine: OrderLineModel;
    order: OrderModel;
    wishListSettings?: WishListSettingsModel;
    vmiBinDetailsPageUrl?: string;
    styles: OrderLinesListStyles;
    canReorderItems: boolean;
    setAddToListModalIsOpen: (parameter: {
        productInfos?: Omit<ProductInfo, "productDetailPath">[];
        modalIsOpen: boolean;
    }) => void;
    addToWishList: (parameter: AddToWishListParameter & HasOnComplete) => void;
    reorder: (parameter: { onSuccess?: () => void }) => void;
    setSpoolModalOpen: (parameter: { isOpen: boolean; id: string; buyAgainOrderLineId?: string }) => void;
    customLoadStoolItems: (parameter: { items: ProductPriceParameter[] }) => unknown;
    addToCart: (parameter: any) => unknown;
    isAuthenticated: boolean;
    billTo: any;
    isCartUpdating: boolean;
    cartLines: CartLineModel[];
}) => {
    const {
        orderLine,
        order,
        wishListSettings,
        vmiBinDetailsPageUrl,
        styles,
        setAddToListModalIsOpen,
        addToWishList,
        canReorderItems,
        reorder,
        addToCart,
        isAuthenticated,
        setSpoolModalOpen,
        customLoadStoolItems,
        billTo,
        isCartUpdating,
        cartLines,
    } = props;
    const toasterContext = React.useContext(ToasterContext);
    const addToListClickHandler = () => {
        if (!wishListSettings) {
            return;
        }

        const productInfo = {
            productId: orderLine.productId,
            qtyOrdered: orderLine.qtyOrdered,
            unitOfMeasure: orderLine.unitOfMeasure,
        };

        if (!wishListSettings.allowMultipleWishLists) {
            addToWishList({
                productInfos: [productInfo],
                onSuccess: () => {
                    toasterContext.addToast({ body: siteMessage("Lists_ProductAdded"), messageType: "success" });
                },
            });
            return;
        }

        setAddToListModalIsOpen({ modalIsOpen: true, productInfos: [productInfo] });
    };
    let isBuyAgainDisabledBtn = false;

    const buyAgainHandler = async () => {
        isBuyAgainDisabledBtn = true;
        let orderlineNewProps = {};

        // Verndale Code
        if (isAuthenticated) {
            const sapAccountNumber =
                isAuthenticated && billTo ? billTo.customerNumber.replace(/^0+/, "") : "__IS_LOADING__";

            setGA4Event([], "buy_again", { sapAccountNumber });
        }


        setSpoolModalOpen({
            isOpen: false,
            buyAgainOrderLineId: orderLine.id,
            id: "order-history",
        });

        const itemsWithStools = (await customLoadStoolItems({
            items: [
                {
                    productId: orderLine.productId,
                    unitOfMeasure: orderLine.unitOfMeasure,
                    qtyOrdered: orderLine.qtyOrdered,
                },
            ],
        })) as ProductPriceParameter[];

        if (itemsWithStools && itemsWithStools.length > 0 && cartLines && cartLines.length > 0) {
            // if this product already exists in the cartlines with the properties spool,
            // we dont add it again, instead we display a modal            
                const cartLine = cartLines.find(
                    cl =>
                        cl.productId === orderLine.productId &&
                        cl.properties &&
                        cl.properties.spoolLength !== undefined &&
                        cl.properties.spoolSelected !== undefined,
                );                   
        }

        orderlineNewProps = {
            spoolSelected: "1",
            spoolLength:
                typeof orderLine.qtyOrdered === "number" && orderLine.qtyOrdered > 0
                    ? `${orderLine.qtyOrdered}`
                    : "1",
        };

        const cartline = (await addToCart({
            productId: orderLine.productId,
            qtyOrdered: orderLine.qtyOrdered,
            unitOfMeasure: orderLine.unitOfMeasure,
            properties: orderlineNewProps,
            additionalQueryStringParameters: orderLine.properties,
        })) as CartLineModel;

        if (cartline) {
            toasterContext.addToast({
                body: <ProductAddedToCartMessage isQtyAdjusted={cartline.isQtyAdjusted} multipleProducts={false} />,
                messageType: "success",
            });
        }

        isBuyAgainDisabledBtn = false;
    };

    const isShareEntity = getIsShareEntity();

    const isCMI = (orderLine as any).productInfo?.properties?.isCmiProduct?.toLowerCase() === "true";

    // verndale customization ticket :IEIXOP-4587 removed validation for CMI product 
    // as the CMI and Parent should behave the same
    const canBuyAgain = canReorderItems && orderLine.canAddToCart && !(orderLine as any).productInfo.quoteRequired;

    const canRFQ = isAuthenticated && orderLine.canAddToCart && (orderLine as any).productInfo.quoteRequired;


    return (
        <GridContainer
            {...styles.orderLineCardGridContainer}
            data-test-selector={`orderLineCard${orderLine.productId}`}
        >
            <GridItem {...styles.orderLineCardImageGridItem}>
                {orderLine.productUri && orderLine.isActiveProduct && !order.vmiLocationId && (
                    <Link {...styles.productImageLink} href={orderLine.productUri}>
                        <LazyImage
                            {...styles.orderLineCardImage}
                            src={orderLine.mediumImagePath}
                            isShareEntity={isShareEntity}
                        />
                    </Link>
                )}
                {orderLine.productUri && orderLine.isActiveProduct && order.vmiLocationId && orderLine.isActiveVmiBin && (
                    <Link
                        {...styles.productImageLink}
                        href={`${vmiBinDetailsPageUrl}?locationId=${order.vmiLocationId}&id=${orderLine.vmiBinId}`}
                    >
                        <LazyImage
                            {...styles.orderLineCardImage}
                            src={orderLine.mediumImagePath}
                            isShareEntity={isShareEntity}
                        />
                    </Link>
                )}
                {orderLine.productUri &&
                    orderLine.isActiveProduct &&
                    order.vmiLocationId &&
                    !orderLine.isActiveVmiBin && (
                        <LazyImage
                            {...styles.orderLineCardImage}
                            src={orderLine.mediumImagePath}
                            isShareEntity={isShareEntity}
                        />
                    )}

                {!orderLine.isActiveProduct && !orderLine.productUri && (
                    <LazyImage
                        {...styles.orderLineCardImage}
                        src={orderLine.mediumImagePath}
                        isShareEntity={isShareEntity}
                    />
                )}
            </GridItem>
            <GridItem {...styles.orderLineCardInfoGridItem}>
                <GridContainer {...styles.orderLineCardInfoGridContainer}>
                    <OrderLineProductInfo orderLine={orderLine} styles={styles} vmiLocationId={order.vmiLocationId} />
                    <OrderLineInfo orderLine={orderLine} order={order} styles={styles} />
                </GridContainer>
            </GridItem>
            <GridItem {...styles.orderLineCardAddToListGridItem}>
                <Hidden {...styles.orderLineCardActionsWide}>
                    {orderLine.canAddToWishlist && isAuthenticated && (
                        <OverflowMenu position="end" {...styles.overflowMenu}>
                            {canBuyAgain ? (
                                <Clickable
                                    {...styles.addToListClickable}
                                    onClick={buyAgainHandler}
                                    disabled={isCartUpdating || isBuyAgainDisabledBtn}
                                >
                                    {translate("Buy Again")}
                                </Clickable>
                            ) : canRFQ ? (
                                <Clickable {...styles.addToListClickable} onClick={buyAgainHandler}>
                                    {translate("Request Quote")}
                                </Clickable>
                            ) : null}

                            <Clickable {...styles.addToListClickable} onClick={addToListClickHandler}>
                                {translate("Add to List")}
                            </Clickable>
                        </OverflowMenu>
                    )}
                </Hidden>
                <Hidden {...styles.orderLineCardActionsNarrow}>
                    {canBuyAgain ? (
                        <>
                            <Button
                                {...styles.orderLineCardAddToListButton}
                                onClick={buyAgainHandler}
                                disabled={isCartUpdating || isBuyAgainDisabledBtn}
                            >
                                {translate("Buy Again")}
                            </Button>
                            <Spacer height={10} heightMobile={10} />
                        </>
                    ) : canRFQ ? (
                        <>
                            <Button {...styles.orderLineCardAddToListButton} onClick={buyAgainHandler}>
                                {translate("Request Quote")}
                            </Button>
                            <Spacer height={10} heightMobile={10} />
                        </>
                    ) : null}
                    {orderLine.canAddToWishlist && (
                        <Button
                            {...styles.orderLineCardAddToListButton}
                            variant="tertiary"
                            onClick={addToListClickHandler}
                        >
                            {translate("Add to List")}
                        </Button>
                    )}
                </Hidden>
            </GridItem>
        </GridContainer>
    );
};
const OrderLinesList: FC<Props> = ({
    order,
    wishListSettings,
    vmiBinDetailsPageUrl,
    setAddToListModalIsOpen,
    addToWishList,
    extendedStyles,
    canReorderItems,
    reorder,
    addToCart,
    isAuthenticated,
    setSpoolModalOpen,
    customLoadStoolItems,
    billTo,
    isCartUpdating,
    cartLines,
}) => {
    const [styles] = React.useState(() => mergeToNew(orderLinesListStyles, extendedStyles));

    if (!order || !order.orderLines) {
        return null;
    }

    return (
        <GridContainer {...styles.gridContainer}>
            <GridItem {...styles.titleGridItem}>
                <Typography {...styles.titleText}>{translate("Order Summary")}</Typography>
            </GridItem>
            {order.orderLines.map(orderLine => (
                <CardContainer {...styles.orderLineCardContainer} key={orderLine.id}>
                    <GridItem {...styles.orderLineCardGridItem}>
                        <OrderLineCard
                            orderLine={orderLine}
                            order={order}
                            wishListSettings={wishListSettings}
                            vmiBinDetailsPageUrl={vmiBinDetailsPageUrl}
                            styles={styles}
                            setAddToListModalIsOpen={setAddToListModalIsOpen}
                            addToWishList={addToWishList}
                            canReorderItems={canReorderItems}
                            reorder={reorder}
                            addToCart={addToCart}
                            isAuthenticated={isAuthenticated}
                            setSpoolModalOpen={setSpoolModalOpen}
                            customLoadStoolItems={customLoadStoolItems}
                            billTo={billTo}
                            isCartUpdating={isCartUpdating}
                            cartLines={cartLines ?? []}
                        />
                    </GridItem>
                </CardContainer>
            ))}
        </GridContainer>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(OrderLinesList as any);
